import Loader from 'components/Loader';
import Cookies from 'js-cookie';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IncomingCall, callerIdService } from 'services/caller-id';
import { accentCssVars, CALLER_ID_THEME } from './accent-tokens';
import CallBanner from './CallBanner';
import CallerToast from './CallerToast';
import { formatPhone } from 'utils/formatPhone';
import { setPendingCallerBooking } from './caller-booking-handoff';
import { BANNER_DURATION_SEC, callerRefFromIncoming } from './use-caller-id';
import './caller-id.scss';

const TOAST_DURATION_MS = 3200;

// Owner-only, app-wide incoming-call banners (mounted in AdminLayout, outside
// any specific page). Book navigates to /calendar with the caller in location
// state; CallerIdOverlay there picks it up and opens the caller booking flow.
// The rest of the Caller ID UI (FAB/sheet/booking page) stays calendar-only.
const CallerBannerHost = () => {
  const _history = useHistory();
  const isOwner = !!+(Cookies.get(process.env.REACT_APP_COOKIE_KEY_ISMANAGER!) ?? '0');

  const [ringing, setRinging] = useState<IncomingCall[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [smsLoading, setSmsLoading] = useState(false);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  const smsInFlight = useRef<Record<string, boolean>>({});
  const mounted = useRef(true);

  useEffect(() => {
    if (!isOwner) return;
    mounted.current = true;
    const unsubscribe = callerIdService.subscribeIncomingCalls(call => {
      setRinging(r => [...r, call]);
      timers.current[call.id] = setTimeout(() => {
        setRinging(r => r.filter(c => c.id !== call.id));
        delete timers.current[call.id];
      }, BANNER_DURATION_SEC * 1000);
    });
    return () => {
      mounted.current = false;
      unsubscribe();
      Object.values(timers.current).forEach(clearTimeout);
      timers.current = {};
      clearTimeout(toastTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOwner) return null;

  const removeBanner = (id: string) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setRinging(r => r.filter(c => c.id !== id));
  };

  const showToast = (msg: string) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  };

  const sms = (call: IncomingCall) => {
    if (smsInFlight.current[call.id]) return;
    smsInFlight.current[call.id] = true;
    setSmsLoading(true);
    callerIdService.sendSms(call.id, call.phone)
      .then(() => {
        if (!mounted.current) return;
        removeBanner(call.id);
        showToast(`SMS sent to ${formatPhone(call.phone)}`);
      })
      .catch(() => {
        if (mounted.current) showToast(`Failed to send SMS to ${formatPhone(call.phone)}`);
      })
      .finally(() => {
        delete smsInFlight.current[call.id];
        if (mounted.current) setSmsLoading(false);
      });
  };

  const book = (call: IncomingCall) => {
    removeBanner(call.id);
    // module hand-off instead of location state (HashRouter-safe); the push
    // still changes location.key so the overlay re-checks even when already
    // on /calendar
    setPendingCallerBooking(callerRefFromIncoming(call));
    _history.push('/calendar');
  };

  return (
    <div className="cid-root" style={accentCssVars(CALLER_ID_THEME)}>
      {ringing.map((call, i) => (
        <CallBanner key={call.id} call={call} index={i} onSms={sms} onBook={book} />
      ))}
      {toast && <CallerToast message={toast} />}
      {smsLoading && <Loader />}
    </div>
  );
};

export default CallerBannerHost;
